/**
 * wizardScan.ts — the live half of the web wizard's cover-photo scan
 * (card 1218418724730535, Blake's approved design).
 *
 * app.js owns the UI and calls
 * `window.__gbzWizardScan(photo, category, supportPhoto)`; this module owns the
 * network. Same split wizardPublish uses, and it is what keeps app.js — which
 * also ships as the standalone demo — free of anything needing a token.
 *
 * 📌 The third argument is not decoration and this header used to omit it. A
 * caller that forgets it still TYPECHECKS, because supportPhoto is optional —
 * which is exactly how app.js's "Try again" handler spent its whole life
 * buying scans with one photo while the first attempt had sent two. Nothing
 * was red; the evidence was simply quietly worse. There are exactly TWO call
 * sites today — commitScanAsk and the #wzscan-retry handler, both in app.js —
 * and anyone adding a third should read the comment on the second before
 * writing one.
 *
 * ── THE THREE SIGNALS ARE REAL, AND THERE ARE ONLY THREE ───────────────────
 * The board shows three segments. They are exactly the three things this code
 * actually learns:
 *
 *   uploading → the POST /api/upload round-trip is in flight
 *   scanning  → that returned a URL and POST /api/ai/scan-photo returned a job
 *               id, so a job exists and is queued or processing
 *   done      → the poll resolved with a result
 *
 * ⚠️ THESE ARE THE PHASE NAMES, NOT THE SELLER-FACING LABELS. This header used
 * to assert the labels too — "Blake's board shows Uploading / Scanning /
 * Ready" — and it was never true of the shipped code: app.js rendered
 * "Upload / Scan / Ready" from the same commit that added the claim, and round
 * 2 (2026-09-13) ruled "Uploaded / Scanning / Ready". Three records, three
 * different first labels, none of which agreed with another.
 *
 * The wording now lives in exactly one place, app.js's renderWzScan, and this
 * file no longer competes with it. 🪤 The open question that survived, raised
 * with Blake and NOT settled here: the bar's index is the segment currently
 * RUNNING, so during upload the running segment reads "Uploaded" — past tense
 * for something still in flight — and on done the completed second segment
 * reads "Scanning". Only "Uploading" makes the running case read correctly.
 * That is a copy ruling, so it is his.
 *
 * ⚠️ `pollJobResult` does NOT surface per-tick job status — it loops internally
 * and resolves only on done, swallowing every queued/processing response on the
 * way. So "scanning" here means "a job id is in hand and the poll has not
 * resolved", which is queued-or-processing BY CONSTRUCTION rather than by
 * observation. That matters to anyone who later wants a fourth segment: there
 * is no finer signal available to hang one on, and the card's own instruction
 * is that the model answers in one reply. No sub-stage may be invented.
 *
 * ── THE UPLOAD IS BORROWED, NEVER REPEATED ─────────────────────────────────
 * The cover photo must be uploaded ONCE and the same URL reused at publish.
 * Rather than reimplement (and separately cache) the upload, this calls the
 * uploader wizardPublish already owns and lends as `__gbzUploadWizardPhoto`.
 * That module keys its cache by the source photo string, so publish later finds
 * this upload already done and skips it — the requirement met without touching
 * publish's idempotency handling.
 */
import type { AuthFetch } from "../lib/importHub";
import { resumeScanJob, startScanJob, type ScanOutcome, type ScanSuggestion } from "../lib/scan";
import { scanEarnedItsPrice, scanIdentifiedTheItem } from "../lib/scanConfidence";

/**
 * 🔑 EVERY STATE NAMES THE PHOTO IT BELONGS TO.
 *
 * A scan is a scan OF SOMETHING. Carrying the source photo string lets app.js
 * render the banner only while that photo is still the cover, which is the
 * honest condition and also disposes of three separate staleness bugs at once:
 * the seller abandoning the wizard and starting a new one, removing or
 * replacing the cover mid-scan, and reordering the thumbnails so a different
 * photo becomes the cover. In all three the suggestion no longer describes what
 * is being listed, and in all three the banner simply stops applying — with no
 * reset plumbing threaded through the thirteen places the draft is recreated.
 */
/** `support` is the second photo, set only once it actually went with the scan (Blake, 2026-09-15). */
type ScanOf = { photo: string; category: string; support?: string | null };

/**
 * ── THE BARS ARE APPLIED HERE, NOT IN app.js ──────────────────────────────
 *
 * app.js is a hand-written classic script that cannot import anything, and
 * these are policy decisions with a real argument behind them and real tests
 * on them. So the suggestion that crosses the seam is ALREADY gated: below the
 * identity bar there is no title and no brand to write, below the price bar
 * there is no value. app.js writes whatever it is handed and asks no questions.
 *
 * 🔑 A GATED-OUT FIELD IS INDISTINGUISHABLE FROM AN ABSENT ONE, deliberately.
 * It renders as the ordinary empty box every unscanned listing starts with,
 * not as a new error state — which is mobile's rule too, and the reason a
 * low-confidence scan degrades into a flow the seller already knows.
 *
 * ⚠️ `condition` AND `category` ARE NOT GATED ON confidence, and must not be.
 * That score is the model's claim about having identified the SPECIFIC item,
 * and neither is downstream of it: a model can be at 5% on WHICH shoe this is
 * and completely right that it is footwear. Those two fail closed a layer
 * earlier instead — aiTools.ts no longer SUBSTITUTES a value for an unusable
 * answer, so an absent one arrives absent.
 *
 * Reconstructed field by field rather than spread, so a server change widening
 * the payload cannot smuggle an ungated field through this function.
 */
function gateSuggestion(sug: ScanSuggestion): {
  suggestion: ScanSuggestion;
  unidentified: { readSlug: string | null } | null;
} {
  const identified = scanIdentifiedTheItem(sug.confidence);
  return {
    suggestion: {
      title: identified ? sug.title : undefined,
      brand: identified ? sug.brand : undefined,
      category: sug.category,
      subcategory: sug.subcategory,
      /* `suggestedValue` keeps its OWN type check alongside the confidence
         bar: they are different values on different scales, and collapsing the
         two would be the same conflation the two confidence scales are kept
         apart from. */
      suggestedValue:
        typeof sug.suggestedValue === "number" && scanEarnedItsPrice(sug.confidence)
          ? sug.suggestedValue
          : undefined,
      condition: sug.condition,
      confidence: sug.confidence,
    },
    /* SUBCATEGORY FIRST, because after the seller's tap it is the only
       classification the model was asked for — and it is the better sentence.
       "It looks like Trading Cards" tells the seller something; "it looks like
       Collectibles" reads back the tap they just made. `category` is the
       fallback for a scan started without a tap. */
    unidentified: identified ? null : { readSlug: sug.subcategory ?? sug.category ?? null },
  };
}

/** What app.js renders. One closed set — every branch below lands on one. */
export type WizardScanState =
  | (ScanOf & { phase: "uploading" })
  | (ScanOf & { phase: "scanning" })
  | (ScanOf & {
      phase: "done";
      /** Already past the confidence bars — see gateSuggestion. */
      suggestion: ScanSuggestion;
      /**
       * Set when the model did NOT clear the identity bar, carrying the one
       * thing it could still see. app.js turns it into a plain caption —
       * "it looks like Trading Cards" — because an empty title box is honest
       * but mute: on its own it cannot say whether the scan failed, is still
       * running, or looked and could not tell.
       */
      unidentified: { readSlug: string | null } | null;
    })
  | (ScanOf & { phase: "rateLimited" })
  | (ScanOf & { phase: "unresolved"; jobId: string })
  | (ScanOf & { phase: "failed" });

type UploadedPhoto = { url: string; renditionUrl: string };

interface ScanWindow extends Window {
  __gbzWizardScan?: (photo: string, category: string, supportPhoto?: string | null) => void;
  __gbzResumeWizardScan?: (photo: string, category: string, jobId: string) => void;
  __gbzStopWizardScan?: () => void;
  /** Published by app.js's bridge; writes the state and repaints. */
  __gbzSetWizardScanState?: (s: WizardScanState | null) => void;
  /** Lent by installWizardPublish — the shared, cached uploader. */
  __gbzUploadWizardPhoto?: (photo: string) => Promise<UploadedPhoto | null>;
}

export function installWizardScan(
  win: Window,
  authFetch: AuthFetch,
  opts: { intervalMs?: number; maxAttempts?: number } = {},
): () => void {
  const w = win as ScanWindow;

  /**
   * 🔑 THE RUN TOKEN, and why "Stop" is not an abort.
   *
   * The vision job is PAID FOR the moment it is enqueued. Once it is, aborting
   * the poll would not refund it, and re-scanning that photo afterwards would
   * buy a second answer to a question already asked — the exact double-charge
   * the `unresolved` outcome exists to prevent.
   *
   * So Stop means two different things either side of that purchase, and the
   * token is what implements both:
   *
   *   BEFORE the POST — the run is still uploading, nothing has been bought,
   *   and the explicit token check below the upload returns before it can be.
   *   A Stop here genuinely costs the seller nothing.
   *
   *   AFTER the POST — the job exists and is paid for. The poll runs to
   *   completion and the ANSWER IS THROWN AWAY, because setState drops every
   *   write whose token has moved. Nothing is cancelled; the seller simply
   *   stops being shown it.
   *
   * The same two guards cover a seller starting a second scan before the first
   * finishes: the older run can never overwrite the newer one, in either
   * arrival order.
   */
  let runToken = 0;

  const setState = (token: number, s: WizardScanState | null) => {
    if (token !== runToken) return;
    w.__gbzSetWizardScanState?.(s);
  };

  /** The one place an outcome becomes a rendered state. Shared by start and
   *  resume so the two can never disagree about what a 429 or a ceiling means. */
  function land(token: number, of: ScanOf, outcome: ScanOutcome): void {
    switch (outcome.status) {
      case "done": {
        const gated = gateSuggestion(outcome.suggestion);
        setState(token, {
          ...of,
          phase: "done",
          suggestion: gated.suggestion,
          unidentified: gated.unidentified,
        });
        return;
      }
      case "rateLimited":
        /* Ruled: the photo is KEPT and the flow degrades to ordinary manual
           entry. Nothing is wrong with the photo or the app — they have simply
           run a lot of scans — so this is its own state, not `failed`, and it
           offers no retry. */
        setState(token, { ...of, phase: "rateLimited" });
        return;
      case "unresolved":
        /* THIS poll gave up; the job may still be running and its answer is
           already bought. Carries the jobId so the only thing offerable is
           resuming that same job, never a fresh scan. */
        setState(token, { ...of, phase: "unresolved", jobId: outcome.jobId });
        return;
      default:
        // "failed" and "error" — nothing left to wait for, so a fresh scan is
        // the honest offer here and ONLY here.
        setState(token, { ...of, phase: "failed" });
    }
  }

  async function run(photo: string, category: string, supportPhoto?: string | null): Promise<void> {
    runToken += 1;
    const token = runToken;
    const of: ScanOf = { photo, category };

    setState(token, { ...of, phase: "uploading" });

    const upload = w.__gbzUploadWizardPhoto;
    if (typeof upload !== "function") {
      /* The uploader is installed alongside this module, so its absence is a
         wiring fault, not a runtime condition. Surfaced as `failed` because
         that is what the seller experiences and there is nothing to resume. */
      setState(token, { ...of, phase: "failed" });
      return;
    }

    let uploaded: UploadedPhoto | null;
    try {
      uploaded = await upload(photo);
    } catch {
      /* uploadOne can reject as well as resolve null — `fetch(photo)` on a
         revoked object URL throws, and so does a body that is not JSON. */
      uploaded = null;
    }
    /* null also covers a rejected MIME: wizardPublish pre-checks the blob type
       because /api/upload reports a HEIC as a bare 500. Either way the photo
       stays on the draft and the seller types the fields themselves. */
    if (!uploaded) {
      setState(token, { ...of, phase: "failed" });
      return;
    }
    if (token !== runToken) return;

    /* ── THE SUPPORT PHOTO IS BEST-EFFORT, AND THAT ASYMMETRY IS THE POINT ──
       The cover is the item; the second photo is EVIDENCE about it. So a
       failed cover upload ends the scan (there is nothing to read), while a
       failed support upload just means the model gets one photo instead of
       two — which is the scan that shipped yesterday, not an error state.
       Wrapped and discarded rather than surfaced for that reason.

       Uploaded through the same borrowed uploader, so this photo is cached for
       publish exactly as the cover is: the seller's second photo was going to
       cross the wire at publish anyway, and now it crosses once for both. */
    let supportUrl: string | null = null;
    if (supportPhoto) {
      try {
        const got = await upload(supportPhoto);
        supportUrl = got ? got.url : null;
      } catch {
        supportUrl = null;
      }
      if (token !== runToken) return;
    }

    /* Named on the state only when it went with the scan, so app.js frames the
       second thumb and says "photos" exactly when the model is reading two. */
    const scanOf: ScanOf = { ...of, support: supportUrl ? supportPhoto ?? null : null };
    setState(token, { ...scanOf, phase: "scanning" });
    land(token, scanOf, await startScanJob(authFetch, uploaded.url, category, supportUrl, opts));
  }

  /**
   * Keep waiting on a job that is already enqueued and already paid for.
   *
   * Routes through `resumeScanJob`, whose whole guarantee is that it makes no
   * POST to /api/ai/scan-photo. Note it re-enters the `scanning` phase and NOT
   * `uploading`: there is nothing to upload, the URL is long since stored on
   * the job, and showing an upload bar again would be a second lie about what
   * the machine is doing.
   */
  async function resume(photo: string, category: string, jobId: string): Promise<void> {
    runToken += 1;
    const token = runToken;
    const of: ScanOf = { photo, category };

    setState(token, { ...of, phase: "scanning" });
    land(token, of, await resumeScanJob(authFetch, jobId, opts));
  }

  /* Deliberately not awaited: the card's rule is that the scan NEVER blocks.
     Next, the add-more box and every other control keep working while it runs,
     and the result lands on the banner either way. */
  const start = (photo: string, category: string, supportPhoto?: string | null) =>
    void run(photo, category, supportPhoto);
  const keepWaiting = (photo: string, category: string, jobId: string) =>
    void resume(photo, category, jobId);

  const stop = () => {
    runToken += 1; // orphans the in-flight run's writes; see the run token above
    w.__gbzSetWizardScanState?.(null);
  };

  w.__gbzWizardScan = start;
  w.__gbzResumeWizardScan = keepWaiting;
  w.__gbzStopWizardScan = stop;

  return () => {
    /* Uninstalling orphans the in-flight run exactly as Stop does. Without
       this, a paid job still polling at teardown kept a live token and, when it
       answered, wrote into whatever banner setter was installed by then. Safe
       on a LATE teardown too: this token belongs to this install alone. */
    runToken += 1;
    /* Each guarded on identity so a teardown arriving after a re-install never
       deletes the newer install's functions. */
    if (w.__gbzWizardScan === start) delete w.__gbzWizardScan;
    if (w.__gbzResumeWizardScan === keepWaiting) delete w.__gbzResumeWizardScan;
    if (w.__gbzStopWizardScan === stop) delete w.__gbzStopWizardScan;
  };
}
